import { useCallback } from 'react'
import { toast } from 'sonner'
import type { PropostaItem, PropostaItemTipo } from '@/types'
import {
  buildProposalItemFromCatalog,
  buildSlideLayoutForProposalItem,
  findCatalogPlanForItem,
  getScopeContentSignature,
  getScopeFromCatalog,
  syncProposalItemFromCatalog,
} from '@/utils/planCatalog'
import { generateStudioItemId, type AvailableItem } from './proposalStudioConstants'
import { saveLayoutAsPlanDefault } from './proposalStudioSave'
import type { StudioActionDeps } from './proposalStudioActionTypes'

export function useProposalStudioCatalogActions(deps: StudioActionDeps) {
  const {
    catalogPlans,
    proposalItems,
    activeIndex,
    layout,
    setProposalItems,
    setLayout,
    scopeLayoutSignatureRef,
    loadCatalog,
  } = deps

  const handleAddItem = useCallback(
    (avail: AvailableItem) => {
      const master = catalogPlans.find((p) => p.id === avail.id)
      if (!master) {
        toast.error('Plano não encontrado no catálogo. Atualize em Configuração → Financeiro → Planos.')
        return
      }
      const newItem: PropostaItem = {
        ...buildProposalItemFromCatalog(master),
        id: generateStudioItemId(),
        quantidade: 1,
      }
      const scope = getScopeFromCatalog(master)
      const planLayout = getScopeContentSignature(scope)
      scopeLayoutSignatureRef.current = `${master.id}:${planLayout}`
      setProposalItems((prev) => [...prev, newItem])
      setLayout(buildSlideLayoutForProposalItem(newItem, catalogPlans))
      toast.success(`${master.nome} adicionado (escopo do catálogo)`)
    },
    [catalogPlans, scopeLayoutSignatureRef, setProposalItems, setLayout],
  )

  const handleResyncActiveFromCatalog = useCallback(() => {
    const item = proposalItems[activeIndex]
    if (!item) return
    const master = findCatalogPlanForItem(item, catalogPlans)
    if (!master) {
      toast.error('Este item não está vinculado a um plano do catálogo.')
      return
    }
    scopeLayoutSignatureRef.current = ''
    const syncedItem = syncProposalItemFromCatalog(item, master, {
      preserveCommercial: true,
      forceScopeSync: true,
    })
    setProposalItems((prev) =>
      prev.map((p, i) => (i === activeIndex ? syncedItem : p)),
    )
    setLayout(buildSlideLayoutForProposalItem(syncedItem, catalogPlans))
    toast.success('Escopo e layout atualizados a partir de Configuração → Planos')
  }, [proposalItems, activeIndex, catalogPlans, scopeLayoutSignatureRef, setProposalItems, setLayout])

  const handleSaveLayoutAsPlanDefault = useCallback(
    () =>
      void saveLayoutAsPlanDefault({
        proposalItems,
        activeIndex,
        catalogPlans,
        layout,
        scopeLayoutSignatureRef,
        reloadCatalog: loadCatalog,
      }),
    [proposalItems, activeIndex, catalogPlans, layout, scopeLayoutSignatureRef, loadCatalog],
  )

  const handleAddCustomItem = useCallback(() => {
    const nome = prompt('Nome do item:')
    if (!nome) return
    const valor = Number(prompt('Valor unitário:') || '0')
    const tipo = (prompt('Tipo (plano/servico/avulso):', 'servico') || 'servico') as PropostaItemTipo
    if (!['plano', 'servico', 'avulso'].includes(tipo)) {
      toast.error('Tipo inválido')
      return
    }
    const newItem: PropostaItem = {
      id: generateStudioItemId(),
      descricao: nome,
      tipo,
      quantidade: 1,
      valorUnitario: valor,
      total: valor,
      duration: tipo === 'plano' ? 12 : 1,
      showBonus: false,
      bonus: '',
      services: [],
      pricing: {
        total: tipo === 'plano' ? valor * 12 : valor,
        entry: 0,
        installments: tipo === 'plano' ? 12 : 1,
        installmentValue: valor,
        recorrente: tipo === 'plano' || tipo === 'servico',
      },
    }
    setProposalItems((prev) => [...prev, newItem])
  }, [setProposalItems])

  return { handleAddItem, handleResyncActiveFromCatalog, handleSaveLayoutAsPlanDefault, handleAddCustomItem }
}
